
import {
    Component,
    forwardRef,
    KeyValueDiffers,
    Inject,
    OnChanges,
    ViewChild,
    OnInit,
    ElementRef,
    ChangeDetectorRef,
    HostListener,
    OnDestroy
} from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import * as htmlToImage from 'html-to-image';
import * as papaparse from 'papaparse';
import * as am4core from '@amcharts/amcharts4/core';

import { download } from '../../../Misc/helpers/download/download.helper';
import { BlockComponent } from '../../../PageBuilder/components/Block/block.component';
import { SeriesModel } from '../../../Charts/models/series.model';
import { AbstractPageBuilderService } from '../../../PageBuilder/services/abstract-page-builder.service';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { ReportBlockTypeEnum } from '../../enums/report-block-type.enum';
import { ReportsPageBuilderService } from '../../services/reports-page-builder.service';
import { ChartBlockComponent } from '../ChartBlock/chart-block.component';
import { ReportsSeriesPreviewService } from '../../services/report-series-preview.service';
import { ReportsChartOptionsService } from '../../services/reports-chart-options.service';
import { chartColors } from '../../../Charts/enums/chart-colors.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { MonitorConfigService } from '../../../MonitorShared/services/monitor-config.service';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { ClickOutsideService } from '../../../PageBuilder/services/click-outside.service';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { ReportsChartDetailService } from '../../services/reports-chart-detail.service';
import { printHelper } from '../../../Misc/helpers/print/print.helper';
import { ChartExportEventModel } from '../../../Charts/models/chart-export-event.model';
import { TopTermEvent, TopTermsChartComponent } from '../../../Charts/components/TopTermsChart/top-terms-chart.component';
import { SocialChartDetailsDialogService } from '../../../../Social/Intelligence/services/social-chart-details-dialog.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { WordCloudChartOptionsModel } from '../../../Charts/models/WordCloudChart/word-cloud-chart-options.model';
import { ChartMetricsEnum } from '../../../../Common/Reports/enums/chart-metrics.enum';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { MentionSearchCriteriaModel } from '../../../../Monitor/Shared/models/mention-search-criteria.model';
import { ChartSizeHelper } from '../../../../Common/Charts/helpers/chart-size.helper';
import { filter } from 'rxjs';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';
import { ReportsChartSeriesConverterService } from '../../services/reports-chart-series-converter.service';
import { ReportsSeriesPreviewNewService } from '../../../../Common/Reports/services/reports-series-preview-new.service';

@Component({
    selector: 'agpr-top-term-chart-block',
    templateUrl: './top-term-chart-block.component.html',
    styleUrls: ['./top-term-chart-block.component.scss'],
    providers: [
        {
            provide: BlockComponent,
            useExisting: forwardRef(() => TopTermChartBlockComponent)
        }
    ],
    standalone: false
})
export class TopTermChartBlockComponent extends ChartBlockComponent implements OnChanges, OnInit, OnDestroy {
    protected exportClass: string = 'agpr-topTermChart--exporting';
    public series: SeriesModel[];
    public sourceRouterLink: string = '';
    public sourceRouterLinkQueryParams: any = {};
    public sourceId: string = '';
    public color: string = chartColors.agprBlue;
    noHeader: boolean;
    small: boolean = false;
    declare chartOptions: WordCloudChartOptionsModel;
    isExporting: boolean = false;

    protected userPreference: PreferencesModel;

    chartResizeObserver = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        const height = entries[0].contentRect.height;
        this.small = width <= 300 || height <= ChartSizeHelper.getHeight(3, 1);
        this.changeDetectorRef.detectChanges();
    });

    @ViewChild('exportMenu') exportMenu;
    @ViewChild('topTermChart') topTermChart: TopTermsChartComponent;

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        public reportSeriesPreviewService: ReportsSeriesPreviewService,
        public reportsChartOptionsService: ReportsChartOptionsService,
        public elementRef: ElementRef,
        public localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public changeDetectorRef: ChangeDetectorRef,
        public userStateService: CurrentUserStateService,
        public monitorConfigService: MonitorConfigService,
        protected userPreferencesService: UserPreferencesService,
        public clickOutsideService: ClickOutsideService,
        public reportsChartDetailService: ReportsChartDetailService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public configService: ConfigService,
        public dataConnectionService: DataConnectionService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsSeriesPreviewNewService: ReportsSeriesPreviewNewService
    ) {
        super(
            differs,
            pageBuilderService,
            reportSeriesPreviewService,
            reportsChartOptionsService,
            localizationService,
            appBaseHref,
            changeDetectorRef,
            userStateService,
            null,
            monitorConfigService,
            userPreferencesService,
            clickOutsideService,
            reportsChartDetailService,
            socialChartDetailsDialogService,
            configService,
            elementRef,
            dataConnectionService,
            reportsChartSeriesConverterService,
            reportsSeriesPreviewNewService
        );
        this.subscriptions.push(
            this.userStateService.getPreferences().pipe(filter(x => !!x)).subscribe(pref => {
                this.userPreference = pref;
            }),
        );
    }
    // we need to add this or else for monitor sources, it will bring you to the page respectively (topics to topics page)
    @HostListener('document:click', ['$event.target']) onClick(event) {
        if (event?.classList?.contains('viewMentionLink')) {
            return false;
        }
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.initSourceRouterLink();
        this.chartResizeObserver.observe(this.elementRef.nativeElement?.children?.[0]);
    }

    ngOnDestroy(): void {
        this.chartResizeObserver.unobserve(this.elementRef.nativeElement?.children?.[0]);
        this.chartResizeObserver.disconnect();
        super.ngOnDestroy();
    }

    ngOnChanges(): void {
        this.updateChart();
        this.noHeader = this.displayElement?.chartHeaderSettings?.hasNoElements?.() ?? false;
    }

    updateChart() {
        this.displayElement = this.block.displayElement as ChartDisplayElementModel;
        if (!this.displayElement || !this.displayElement.series || this.displayElement.series.length < 1) {
            this.series = null;
            return;
        }

        // we don't want this to reset the currency code because it needs to keep consistent with the report
        const currencyCode = this.chartOptions.currencyCode;
        this.chartOptions = this.reportsChartOptionsService.getTopTermsOptions(this.displayElement);
        if (currencyCode){
            this.chartOptions.currencyCode = currencyCode;
        }

        this.renderable = this.block.displayElement.canRender();
        this.color = this.displayElement.styles.colorMap.barColor;

        const series = this.displayElement.series.filter(seriesItem => seriesItem.isValid());
        if (series.length < 1) {
            this.series = null;
            return;
        }
        series.forEach(_series => _series.metric = this.displayElement.metric);
        if (this.preview) {
            this.series = this.reportSeriesPreviewService.getPreviewSeries(
                this.block.type.key as ReportBlockTypeEnum,
                series,
                this.block.displayElement as ChartDisplayElementModel);
        } else {
            this.series = series;
        }

        this.setLabels();
        if ((this.pageBuilderService as ReportsPageBuilderService).mapBlockToChartComponent) {
            (this.pageBuilderService as ReportsPageBuilderService).mapBlockToChartComponent(this.block, this);
        }
    }

    protected setLabels(): void  {
        if (this.displayElement?.series?.[0]?.dateRange
            // For shared report, we already set its relative and absolute date rage when mapping api blocks
            // For date range "ALL" scenario, we cannot do localization in model, so we keep it here
            && (!this.displayElement.series[0].isSharedReport || this.displayElement?.series?.[0]?.dateRange.type === DateQueryTypeEnum.ALL)
        ) {
            this.displayElement.dateRangeLabel = this.reportSeriesPreviewService.dateRangeToString(this.displayElement.series);
        }
    }

    /**
     * Gets the series label from the chart
     */
    public getExportSeriesLabel(): string {
        if (this.series && this.series[0]) {
            return this.series[0].label || '';
        } else {
            return '';
        }
    }

    /**
     * Handles the export button click action, exporting the chart in the selected format
     */
    public async chartExport(event: ChartExportEventModel) {
        this.isExporting = true;
        // this removes the :hover rule that causes the border of the chart to be blue
        const container: HTMLDivElement = this.elementRef.nativeElement.children[0];
        container.classList.remove('agpr-block');

        this.exportMenu.nativeElement.style.display = 'none';

        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;

        let url: string;
        let blob: Blob;

        const displayModel = this.block.displayElement as ChartDisplayElementModel;
        const isAve = displayModel.metric === ChartMetricsEnum.ave;
        const tempData = this.topTermChart.data;
        // There's no easy way to extend the length of the chart image to show all the data.
        // Slice the data to 5 before gererating the image because it gets cut off when data length is more than 5.
        // when image is generated, put the original data back otherwise it will only show 5 .
        this.topTermChart.data = this.topTermChart.data.slice(0, 5);
        requestAnimationFrame(async () => {
            switch (exportType) {
                case 'jpg':
                    url = await htmlToImage.toJpeg(this.elementRef.nativeElement.children[0]);
                    break;

                case 'png':
                    url = await htmlToImage.toPng(this.elementRef.nativeElement.children[0]);
                    break;
                case 'svg':
                    url = await htmlToImage.toSvg(this.elementRef.nativeElement.children[0]);
                    break;

                case 'csv':
                    let termValueLabel = this.localizationService.transform('Reports.term.exporting.termValueLabel');
                    if (isAve){
                        termValueLabel = termValueLabel + `(${ this.userPreference.currency})`;
                    }
                    const csvData = [
                        [
                            this.localizationService.transform('Reports.term.exporting.termLabel'),
                            termValueLabel
                        ]
                    ];

                    if (this.topTermChart.data) {
                        this.topTermChart.data.forEach(item => {
                            csvData.push([item.label, item.count + '']);
                        });
                    }

                    const csvString: string = papaparse.unparse(csvData, {delimiter: ','});

                    blob = new Blob(['\ufeff', csvString], { type: 'text/csv;charset=utf-8;'});
                    break;

                case 'json':
                    const jsonData = {};
                    if (this.topTermChart.data) {
                        this.topTermChart.data.forEach(item => {
                            let value = item.count;
                            if (isAve){
                                value = `${value}${this.userPreference.currency}`;
                            }
                            jsonData[(item.jsonLabel || item.label) as string] = value;

                        });
                    }

                    blob = new Blob([JSON.stringify(jsonData)], { type: 'text/json;charset=utf-8;'});
                    break;

                case 'print':
                    const printElement = this.elementRef.nativeElement.children[0];
                    const printData = await htmlToImage.toSvg(printElement);
                    printHelper.image(printData, document.title, {fit: printElement.offsetHeight <= 1500});
                    break;
            }

            if (blob) {
                download.blob(blob, `${this.displayElement.title}.${exportType}`);

            }

            if (url) {
                download.url(url, `${this.displayElement.title}.${exportType}`);
            }

            this.exportMenu.nativeElement.style.display = 'block';
            container.classList.add('agpr-block');
            this.topTermChart.data = tempData;
            this.isExporting = false;
        });

    }

    /**
     * Gets the export image for the full report pdf export
     */
    public async getFullExportImage(width: number, height: number): Promise<string> {
        const el: HTMLDivElement = this.elementRef.nativeElement.children[0];
        const classes: DOMTokenList = this.elementRef.nativeElement.children[0].classList;

        el.style.border = 'none';


        // Sets a bunch of styles for scaling to the page size of the pdf
        classes.add(this.exportClass);
        el.style.width = width + 'px';
        el.style.minWidth = width + 'px';
        el.style.height = 'auto';
        el.style.minHeight = 'auto';
        el.style.flex = 'unset';

        const png = await htmlToImage.toPng(this.elementRef.nativeElement.children[0]);

        // Resets the styles to the original
        el.style.width = '';
        el.style.minWidth = '';
        el.style.height = '';
        el.style.minHeight = '';
        el.style.flex = '';

        el.style.border = `1px solid ${chartColors.agprLightGrey}`;
        classes.remove(this.exportClass);

        return png;
    }

    viewMentions(event: TopTermEvent): void {
        if (event?.url) {
            this.reportsChartDetailService.viewCoverageAnalysisDialog(event.url, this.block);
        }
    }

    /**
     * Constructs the url to the topic or folder currently being used as a source for this chart.
     * Tags & Tag Groups are also handled, by constructing a url to all saved coverage with appropriate filters
     * applied via queryParams
     */
    public initSourceRouterLink(): void {
        const displayElement = this.block.displayElement as ChartDisplayElementModel;

        if (displayElement?.series?.[0]) {
            const urlParts = displayElement.series[0].getMentionSeriesUrl().split('?filters=');
            this.sourceRouterLink = '/' + urlParts?.[0];
            this.sourceRouterLinkQueryParams = { filters: urlParts?.[1] };
        }
    }

    termSelected(event) {
        this.outputEvent.emit(event);
    }

    formatDateRangeLabel(format: string) {
        this.displayElement.dateRangeLabel = this.reportSeriesPreviewService.dateRangeToString(this.displayElement.series, null, format);
        this.changeDetectorRef.detectChanges();
    }
}
